import { h } from 'preact';
import Store from 'store/store';

import * as jsonPointer from 'utils/jsonPointer';
import { confirm } from 'utils/modal';
import { canUser } from 'utils/roles';

import HubPageEditor from 'components/editor/pages/hubPageEditor';
import Button from 'components/button/button';
import HubNavigation from 'components/editor/hubNavigation/hubNavigation';
import HubFooter from 'components/editor/hubFooter/hubFooter';
import SelectableSection from 'components/editor/selectableSection';
import SimpleField from 'components/editor/settings/simpleField';
import RichTextField from 'components/editor/settings/richTextField';
import MediaField from 'components/editor/settings/mediaField';

import { Sortable, SortableItem, Handle } from 'components/sortable/sortable';

import s from 'components/editor/pages/leadership.sss';

export default class LeadershipEditor extends HubPageEditor {
	constructor(props) {
		super(props);

		this.addLeader = this.addLeader.bind(this);
		this.onDeleteLeaderClick = this.onDeleteLeaderClick.bind(this);
		this.onLeadersSort = this.onLeadersSort.bind(this);
	}

	addLeader() {
		const id = crypto.randomUUID();
		const leadersPath = `${this.props.dataRoot || ''}/content/leaders`;

		// Add new leader with order 0
		jsonPointer.set(Store.get().data, `${leadersPath}/${id}`, {
			name: 'Name',
			title: 'Title',
			group: 'Group',
			picture: 'placeholder.png',
			bio: '',
			order: 0,
		});

		// Shift all existing leaders' orders by +1
		const leaders = jsonPointer.get(Store.get().data, leadersPath);
		for (const [existingId, leader] of Object.entries(leaders)) {
			if (existingId !== id && typeof leader.order === 'number') {
				// Instead of modifying the leader directly, create a new object with the updated order
				const updatedLeader = { ...leader, order: leader.order + 1 };
				jsonPointer.set(Store.get().data, `${leadersPath}/${existingId}`, updatedLeader);
			}
		}

		this.props.select(`/leaders/${id}`);
	}

	async deleteLeader() {
		if (!this.props.activeElement || !jsonPointer.has(this.props.content, this.props.activeElement)) {
			return;
		}
		const path = jsonPointer.parse(`${this.props.dataRoot || ''}/content${this.props.activeElement}`);
		const key = path.pop();
		const parent = jsonPointer.get(Store.get().data, path);
		parent.remove(key);
		this.selectParentLevel();
	}

	async onDeleteLeaderClick() {
		const ok = await confirm('This will delete the selected leadership entry and cannot be undone', { title: 'Delete item?', danger: true, button: 'Delete item' });
		if (!ok) return;
		this.deleteLeader();
	}

	onLeadersSort(e) {
		const listContainer = e.target.closest('[data-leaders]');
		if (!listContainer) return;

		// Correct selector
		const allItems = [...e.target.querySelectorAll('[data-leader]')].map(el => el.dataset.leader);

		// Move item from old index to new
		allItems.splice(e.to, 0, allItems.splice(e.from, 1)[0]);

		// Update store data
		const basePath = this.props.dataRoot || '';
		allItems.forEach((id, index) => {
			jsonPointer.set(Store.get().data, `${basePath}/content/leaders/${id}/order`, index + 1);
		});
	}

	renderVisual() {
		const content = this.props.content;
		const leaders = Object.entries(content?.leaders || {}).sort(([, a], [, b]) => (a.order || 0) - (b.order || 0));
		const pageName = this.getPageName('leadership', 'Leadership');

		return (
			<div class={s.visual}>
				<HubNavigation />
				<div class={s.pageHeader}>
					<div class={s.breadcrumbs}>{this.getPageName('landing', 'Home')} / <span class={s.active}>{pageName}</span></div>
					<div class={s.pageTitle} onClick={() => this.props.select(`/leaders`)}>{pageName}</div>
				</div>
				<SelectableSection section="/leaders" class={s.leaders} {...this.props}>
					{leaders.map(([id, leader]) => (
						<SelectableSection section={`/leaders/${id}`} class={s.leader} {...this.props} key={id}>
							<img class={s.picture} src={leader.picture} alt={leader.name} width="480" height="680" />
							<div class={s.name}>{leader.name}</div>
							<div class={s.title}>{leader.title}</div>
							<div class={s.group}>{leader.group}</div>
						</SelectableSection>
					))}
				</SelectableSection>
				<HubFooter />
			</div>
		);
	}

	renderLeadersEditor() {
		const content = this.props.content;
		const leaders = Object.entries(content?.leaders || {}).sort(([, a], [, b]) => (a.order || 0) - (b.order || 0));
		const readonly = !canUser('write');

		return (
			<div class={s.leaders}>
				<div class={s.note}>Members</div>
				<Sortable class={s.list} onChange={this.onLeadersSort} data-leaders disabled={readonly}>
					{leaders.map(([id, leader]) => (
						<SortableItem class={s.itemWrapper} key={id} data-leader={id}>
							<Handle class={s.handle} />
							<div class={s.item}>
								<span>{leader.name}</span>
								<button class={s.edit} onClick={() => this.props.select(`/leaders/${id}`)}>Edit</button>
							</div>
						</SortableItem>
					))}
				</Sortable>
				<Button primary icon="plus" onClick={this.addLeader} disabled={readonly}>
					Add Leader
				</Button>
			</div>
		);
	}

	renderLeaderEditor() {

		if (!this.props.activeElement || !jsonPointer.has(this.props.content, this.props.activeElement)) {
			return;
		}
		let basePath = `${this.props.dataRoot || ''}/content${this.props.activeElement}`;
		const readonly = !canUser('write');
		return (
			<div class={s.leader}>
				<MediaField
					dark
					label="Leader Photo"
					path={`${basePath}/picture`}
					help={'Aspect ratio: 5:7\nRecommended dimensions: 500x700px'}
					assetData={{ type: 'Leader Photo', aspectRatio: '5:7', recommendedDimensions: '500:700' }}
				/>
				<SimpleField dark label="Name" path={`${basePath}/name`} />
				<SimpleField dark label="Title" path={`${basePath}/title`} />
				<SimpleField dark label="Company" path={`${basePath}/group`} />
				<RichTextField dark label="Bio" path={`${basePath}/bio`} />
				<Button dark text danger onClick={this.onDeleteLeaderClick} disabled={readonly}>
					Delete Leader
				</Button>
			</div>
		);
	}

	renderSettings() {
		let content;
		const nav = [{ label: 'Leadership', path: '/leaders' }];
		const activeElement = this.props.activeElement;
		if (activeElement === '/leaders') {
			content = this.renderLeadersEditor();
		} else if (activeElement?.startsWith('/leaders/')) {
			nav.push({ label: 'Member', path: activeElement });
			content = this.renderLeaderEditor();
		}
		return (
			<div class={s.settings}>
				{this.renderSettingsNavigation(nav)}
				{content}
			</div>
		);
	}
}
